// Region-themed Hadith pool. One short Hadith per region, each chosen to
// resonate locally — community / Ummah for Balkans + Sub-Saharan Africa,
// trade ethics for SE Asia & East Asia, family for South Asia, generosity
// for the Gulf, perseverance through hardship for casia / countries with
// strong diaspora flows, etc.
//
// Each entry ships an Arabic original + an English translation + a citation,
// plus a small `local` map of native-script translations for the 4 beta
// languages (hi/bn/ml/id) and the major established translations (ar/ur/fr/
// ms/tr/ru). Falls back to English when a translation is missing.
//
// Used by <HadithBanner countryCode="..." /> on the homepage. The
// `countryCode → region` mapping comes from `countryGeography.regionOf()`.
import { regionOf } from './countryGeography';

export const REGIONAL_HADITHS = {
  default: {
    arabic: 'بُنِيَ الإِسْلاَمُ عَلَى خَمْسٍ: شَهَادَةِ أَنْ لاَ إِلَهَ إِلاَّ اللَّهُ وَأَنَّ مُحَمَّدًا رَسُولُ اللَّهِ، وَإِقَامِ الصَّلاَةِ، وَإِيتَاءِ الزَّكَاةِ، وَالحَجِّ، وَصَوْمِ رَمَضَانَ.',
    english: 'Islam is built upon five [pillars]: testifying that there is no god but Allah and that Muhammad is the Messenger of Allah, establishing the prayer, paying the Zakat, performing the Hajj, and fasting in Ramadan.',
    citation: 'Sahih al-Bukhari 8 · Sahih Muslim 16',
    local: {
      ar: 'بُنِيَ الإسلامُ على خمسٍ...', // (canonical Arabic is the `arabic` field — `local.ar` repeated for symmetry; renderer falls back to arabic)
      ur: 'اسلام پانچ ستونوں پر قائم ہے: شہادتِ توحید، نماز قائم کرنا، زکوٰۃ ادا کرنا، حج اور رمضان کا روزہ۔',
      hi: 'इस्लाम पाँच स्तंभों पर खड़ा है: तौहीद की गवाही, नमाज़ क़ायम करना, ज़कात अदा करना, हज और रमज़ान के रोज़े।',
      bn: 'ইসলাম পাঁচ স্তম্ভের উপর প্রতিষ্ঠিত: তাওহীদের সাক্ষ্য, সালাত প্রতিষ্ঠা, যাকাত প্রদান, হজ এবং রমজানের রোজা।',
      ml: 'ഇസ്ലാം അഞ്ച് സ്തംഭങ്ങളിൽ സ്ഥാപിതമാണ്: തൗഹീദിന്റെ സാക്ഷ്യം, നമസ്കാരം, സകാത്ത്, ഹജ്ജ്, റമദാൻ വ്രതം.',
      id: 'Islam dibangun di atas lima rukun: syahadat, mendirikan shalat, menunaikan zakat, haji, dan puasa Ramadan.',
      fr: 'L\'Islam est b\u00e2ti sur cinq piliers: la profession de foi, la pri\u00e8re, la zakat, le p\u00e8lerinage et le je\u00fbne du Ramadan.',
      ms: 'Islam dibina atas lima rukun: syahadah, mendirikan solat, menunaikan zakat, haji, dan puasa Ramadan.',
      tr: 'İslâm beş temel üzerine kuruludur: kelime-i şehâdet, namaz, zekât, hac ve oruç.',
      ru: 'Ислам построен на пяти столпах: свидетельстве веры, молитве, закяте, хадже и посте в Рамадан.',
    },
  },

  // Middle East & North Africa — generosity & brotherhood
  mena: {
    arabic: 'الْمُسْلِمُ أَخُو الْمُسْلِمِ، لاَ يَظْلِمُهُ وَلاَ يَخْذُلُهُ.',
    english: 'A Muslim is the brother of another Muslim — he does not oppress him, nor abandon him.',
    citation: 'Sahih al-Bukhari 2442 · Sahih Muslim 2580',
    local: {
      ur: 'مسلمان مسلمان کا بھائی ہے — نہ اس پر ظلم کرتا ہے اور نہ اسے بے یار و مددگار چھوڑتا ہے۔',
      hi: 'मुसलमान मुसलमान का भाई है — न तो उस पर ज़ुल्म करता है, न उसे बेसहारा छोड़ता है।',
      bn: 'একজন মুসলিম অন্য মুসলিমের ভাই — তার উপর জুলুম করে না, এবং তাকে অসহায় ছেড়ে দেয় না।',
      id: 'Seorang Muslim adalah saudara Muslim lainnya — ia tidak menzaliminya, dan tidak meninggalkannya tanpa bantuan.',
    },
  },

  // North Africa — Africa region uses Ummah-brotherhood
  africa: {
    arabic: 'مَثَلُ الْمُؤْمِنِينَ فِي تَوَادِّهِمْ وَتَرَاحُمِهِمْ وَتَعَاطُفِهِمْ مَثَلُ الْجَسَدِ.',
    english: 'The believers in their mutual kindness, compassion and sympathy are like a single body.',
    citation: 'Sahih al-Bukhari 6011 · Sahih Muslim 2586',
    local: {
      ur: 'مومنین کی باہمی محبت، رحمت اور ہمدردی کی مثال ایک جسم کی طرح ہے۔',
      hi: 'मोमिनों के बीच आपसी मेहरबानी, करुणा और हमदर्दी एक शरीर की तरह है।',
      bn: 'মুমিনদের পারস্পরিক সদয়তা, করুণা ও সহানুভূতি একটি দেহের মতো।',
      ml: 'വിശ്വാസികളുടെ പരസ്പര സ്നേഹം, കാരുണ്യം, സഹാനുഭൂതി ഒരു ശരീരം പോലെയാണ്.',
    },
  },

  // South Asia — family and intention
  sasia: {
    arabic: 'إِنَّمَا الأَعْمَالُ بِالنِّيَّاتِ، وَإِنَّمَا لِكُلِّ امْرِئٍ مَا نَوَى.',
    english: 'Verily, deeds are by intentions, and every person shall have only what he intended.',
    citation: 'Sahih al-Bukhari 1 · Sahih Muslim 1907',
    local: {
      ur: 'اعمال کا دارومدار نیتوں پر ہے، اور ہر شخص کے لیے وہی ہے جو اس نے نیت کی۔',
      hi: 'अमलों का दारोमदार नीयतों पर है, और हर शख़्स के लिए वही है जो उसने नीयत की।',
      bn: 'নিশ্চয়ই কর্মসমূহ নিয়তের উপর নির্ভরশীল, এবং প্রত্যেক ব্যক্তি যা নিয়ত করে তাই তার জন্য।',
      ml: 'പ്രവൃത്തികൾ ഉദ്ദേശങ്ങളെ ആശ്രയിച്ചിരിക്കുന്നു, ഓരോ വ്യക്തിക്കും അവൻ ഉദ്ദേശിച്ചതാണ് ലഭിക്കുക.',
    },
  },

  // Southeast Asia — trade ethics
  seasia: {
    arabic: 'التَّاجِرُ الصَّدُوقُ الأَمِينُ مَعَ النَّبِيِّينَ وَالصِّدِّيقِينَ وَالشُّهَدَاءِ.',
    english: 'The truthful and trustworthy merchant will be with the prophets, the truthful, and the martyrs (on the Day of Judgement).',
    citation: 'Sunan al-Tirmidhi 1209 · Sunan Ibn Mājah 2139',
    local: {
      id: 'Pedagang yang jujur dan amanah akan bersama para nabi, orang-orang shiddiq, dan para syuhada (di Hari Kiamat).',
      ms: 'Peniaga yang jujur dan amanah akan bersama para nabi, para siddiqin, dan para syuhada (di Hari Akhirat).',
      hi: 'सच्चा और अमानतदार व्यापारी क़यामत के दिन नबियों, सिद्दीक़ीन और शहीदों के साथ होगा।',
      bn: 'সত্যবাদী ও আমানতদার ব্যবসায়ী কিয়ামতের দিনে নবী, সিদ্দীকীন ও শহীদদের সাথে থাকবে।',
    },
  },

  // Central Asia + Caucasus — perseverance through hardship
  casia: {
    arabic: 'مَا أَصَابَ الْمُسْلِمَ مِنْ نَصَبٍ، وَلاَ وَصَبٍ، وَلاَ هَمٍّ، وَلاَ حَزَنٍ، وَلاَ أَذًى، وَلاَ غَمٍّ، حَتَّى الشَّوْكَةِ يُشَاكُهَا، إِلاَّ كَفَّرَ اللَّهُ بِهَا مِنْ خَطَايَاهُ.',
    english: 'No fatigue, illness, sorrow, sadness, hurt, or distress befalls a Muslim — even a thorn that pricks him — without Allah expiating some of his sins by it.',
    citation: 'Sahih al-Bukhari 5641 · Sahih Muslim 2573',
    local: {
      ru: 'Что бы ни постигло мусульманина — усталость, болезнь, печаль, грусть, обида или беспокойство, даже укол шипа — Аллах непременно списывает с него часть его грехов.',
      tr: 'Müslümana ulaşan hiçbir yorgunluk, hastalık, üzüntü, keder veya elem (bir diken batması bile olsa) yoktur ki, Allah onunla onun günahlarından bir kısmını bağışlamasın.',
    },
  },

  // East Asia — patience and trust
  easia: {
    arabic: 'احْفَظِ اللَّهَ يَحْفَظْكَ، احْفَظِ اللَّهَ تَجِدْهُ تُجَاهَكَ.',
    english: 'Be mindful of Allah and He will protect you. Be mindful of Allah and you will find Him in front of you.',
    citation: 'Sunan al-Tirmidhī 2516 · al-Arba\u02bbīn al-Nawawiyya 19',
    local: {
      // EN fallback dominates for East Asia routes by design.
    },
  },

  // Balkans — community and brotherhood (historical Ottoman Hanafi influence)
  balkans: {
    arabic: 'لاَ يُؤْمِنُ أَحَدُكُمْ حَتَّى يُحِبَّ لِأَخِيهِ مَا يُحِبُّ لِنَفْسِهِ.',
    english: 'None of you truly believes until he loves for his brother what he loves for himself.',
    citation: 'Sahih al-Bukhari 13 · Sahih Muslim 45',
    local: {
      tr: 'Sizden biri kendisi için arzu ettiği şeyi kardeşi için de arzu etmedikçe (gerçek) imana eremez.',
      ur: 'تم میں سے کوئی شخص اس وقت تک مومن نہیں ہو سکتا جب تک کہ وہ اپنے بھائی کے لیے وہی پسند نہ کرے جو اپنے لیے پسند کرتا ہے۔',
    },
  },

  // Europe — knowledge and learning
  europe: {
    arabic: 'مَنْ سَلَكَ طَرِيقًا يَلْتَمِسُ فِيهِ عِلْمًا، سَهَّلَ اللَّهُ لَهُ بِهِ طَرِيقًا إِلَى الجَنَّةِ.',
    english: 'Whoever travels a path seeking knowledge, Allah will make easy for him a path to Paradise.',
    citation: 'Sahih Muslim 2699',
    local: {
      fr: 'Quiconque emprunte un chemin pour y rechercher la connaissance, Allah lui facilitera par cela un chemin vers le Paradis.',
      ur: 'جو شخص علم کی تلاش میں کسی راستے پر چلتا ہے، اللہ اس کے لیے جنت کا راستہ آسان فرما دیتا ہے۔',
    },
  },

  // Americas — small minority + reform; truth-bearing
  americas: {
    arabic: 'الدِّينُ النَّصِيحَةُ.',
    english: 'The religion is sincere counsel (nasīḥah).',
    citation: 'Sahih Muslim 55',
    local: {
      fr: 'La religion est conseil sinc\u00e8re.',
    },
  },
};

/**
 * Resolve the appropriate hadith for a given country code.
 * Falls back to the `default` (zakat 5-pillars hadith) when the country
 * is not mapped to any region.
 */
export const getRegionalHadith = (countryCode) => {
  const region = regionOf(countryCode);
  return REGIONAL_HADITHS[region] || REGIONAL_HADITHS.default;
};
